import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SEO from "@/components/seo/SEO";
import { nieuwsArticles } from "@/data/sample-content";
import { useParams, Link } from "react-router-dom";
import { NewsletterPopup, useNewsletterPopup } from "@/components/NewsletterPopup";
import { SocialShareWidget } from "@/components/SocialShareWidget";
import { Share2, ArrowLeft } from "lucide-react";
import SolarEnergyInfographic from "@/components/content/SolarEnergyInfographic";

const NieuwsPost = () => {
  const { slug } = useParams();
  const { showPopup, closePopup } = useNewsletterPopup();
  const post = nieuwsArticles.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-16">
          <h1 className="text-3xl font-bold mb-4">Nieuwsbericht niet gevonden</h1>
          <p className="text-muted-foreground mb-6">Het opgegeven nieuwsbericht bestaat niet (meer).</p>
          <Link to="/nieuws" className="inline-flex items-center gap-2 text-primary hover:underline">
            <ArrowLeft className="h-4 w-4" />
            Terug naar nieuws
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const category = post.category;

  const jsonLdArticle = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.date,
    author: { "@type": "Organization", name: post.author },
    image: post.imageUrl || undefined,
    keywords: post.keywords.join(", "),
    publisher: {
      "@type": "Organization",
      name: "SOL-VI",
      logo: {
        "@type": "ImageObject",
        url: typeof window !== "undefined" ? `${window.location.origin}/favicon.ico` : "",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": typeof window !== "undefined" ? window.location.href : "",
    },
  } as const;

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: origin + "/" },
      { "@type": "ListItem", position: 2, name: "Nieuws", item: origin + "/nieuws" },
      {
        "@type": "ListItem",
        position: 3,
        name: post.title,
        item: typeof window !== "undefined" ? window.location.href : "",
      },
    ],
  } as const;

  const related = nieuwsArticles
    .filter((p) => p.slug !== post.slug && p.category === post.category)
    .slice(0, 3);

  const handleShare = async () => {
    const url = window.location.href;
    const title = post.title;
    
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback to clipboard
      await navigator.clipboard.writeText(url);
      // You could show a toast here
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={`${post.title} | SOL-VI Nieuws`}
        description={post.excerpt}
        type="article"
        image={post.imageUrl}
        jsonLd={[jsonLdArticle, breadcrumbSchema]}
      />
      <Header />
      
      <main className="container mx-auto px-4 py-12">
        <nav className="mb-6">
          <Link 
            to="/nieuws" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Terug naar nieuws
          </Link>
        </nav>

        <article className="max-w-4xl mx-auto">
          <header className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <Badge className="bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold">
                {category}
              </Badge>
                <span className="text-sm text-muted-foreground font-medium">
                {new Date(post.date).toLocaleDateString("nl-NL")} • {post.readTime} minuten leestijd
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 leading-tight">
              {post.title}
            </h1>
            
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              {post.excerpt}
            </p>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-border pb-8 gap-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                    <span className="text-sm font-bold text-primary">{post.author.charAt(0)}</span>
                  </div>
                  <span className="text-sm font-medium">Door: {post.author}</span>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {post.tags.slice(0, 5).map((tag) => (
                    <span key={tag} className="text-xs px-3 py-1.5 rounded-full bg-accent/20 text-foreground font-medium">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              
               <SocialShareWidget 
                title={post.title}
                description={post.excerpt}
                size="sm"
                className="self-start sm:self-center"
              />
            </div>
          </header>

          {/* Optional hero image */}
          {post.imageUrl && (
            <img
              src={post.imageUrl}
              alt={`Afbeelding voor ${post.title}`}
              loading="lazy"
              className="w-full h-auto rounded-lg border border-border mb-8"
            />
          )}

          {/* Render special component if specified */}
          {post.component === "SolarEnergyInfographic" ? (
            <SolarEnergyInfographic />
          ) : (
            <div className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-foreground prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:border-b prose-h2:border-border prose-h2:pb-3 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4 prose-h3:text-primary prose-p:text-muted-foreground prose-p:leading-relaxed prose-p:mb-6 prose-strong:text-foreground prose-strong:font-semibold prose-ul:space-y-2 prose-li:text-muted-foreground prose-a:text-primary prose-a:font-medium hover:prose-a:underline prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:bg-accent/10 prose-blockquote:pl-6 prose-blockquote:py-4 prose-blockquote:my-8 prose-blockquote:italic">
              {post.content ? (
                post.content.split("\n\n").map((para, idx) => (
                  <p 
                    key={idx} 
                    dangerouslySetInnerHTML={{ 
                      __html: para.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
                    }} 
                  />
                ))
              ) : (
                <p className="text-muted-foreground">
                  Dit artikel heeft nog geen inhoud.
                </p>
              )}
            </div>
          )}

          {/* Call to Action based on category */}
          <section className="mt-12 p-6 bg-gradient-to-r from-primary/5 to-accent/10 rounded-lg">
            <div className="text-center">
              {post.category === 'aanbiedingen' && (
                <>
                  <h3 className="text-xl font-semibold mb-2">Interesse in deze aanbieding?</h3>
                  <p className="text-muted-foreground mb-4">
                    Neem direct contact met ons op voor meer informatie en een persoonlijke offerte.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button 
                      variant="cta"
                      onClick={() => (window as any).Calendly?.initPopupWidget?.({ url: "https://calendly.com/sol-vi/new-meeting?primary_color=ff7800" })}
                    >
                      Plan gesprek
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => (window as any).renderOpenSolarWidget?.()}
                    >
                      Vraag offerte
                    </Button>
                  </div>
                </>
              )}
              
              {post.category === 'projecten' && (
                <>
                  <h3 className="text-xl font-semibold mb-2">Geïnteresseerd in een vergelijkbaar project?</h3>
                  <p className="text-muted-foreground mb-4">
                    Laat ons kijken hoe we u kunnen helpen met uw energie-transitie.
                  </p>
                  <Button 
                    variant="cta"
                    onClick={() => (window as any).Calendly?.initPopupWidget?.({ url: "https://calendly.com/sol-vi/new-meeting?primary_color=ff7800" })}
                  >
                    Gratis energieadvies
                  </Button>
                </>
              )}
              
              {(post.category === 'partnerships' || post.category === 'bedrijfsupdates') && (
                <>
                  <h3 className="text-xl font-semibold mb-2">Wilt u meer weten?</h3>
                  <p className="text-muted-foreground mb-4">
                    Neem contact met ons op voor meer informatie over onze diensten en mogelijkheden.
                  </p>
                  <Button 
                    variant="cta"
                    onClick={() => (window as any).Calendly?.initPopupWidget?.({ url: "https://calendly.com/sol-vi/new-meeting?primary_color=ff7800" })}
                  >
                    Contact opnemen
                  </Button>
                </>
              )}
              
              {post.category === 'events' && (
                <>
                  <h3 className="text-xl font-semibold mb-2">Aanmelden voor dit event?</h3>
                  <p className="text-muted-foreground mb-4">
                    Meld u direct aan of neem contact met ons op voor meer informatie.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button variant="cta">
                      Aanmelden
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => (window as any).Calendly?.initPopupWidget?.({ url: "https://calendly.com/sol-vi/new-meeting?primary_color=ff7800" })}
                    >
                      Meer info
                    </Button>
                  </div>
                </>
              )}
            </div>
          </section>
        </article>

        {/* Related News */}
        {related.length > 0 && (
          <section className="mt-16 max-w-4xl mx-auto">
            <h2 className="text-2xl font-semibold mb-6">Gerelateerd nieuws</h2>
            <div className="grid gap-6 md:grid-cols-3">
              {related.map((relatedPost) => (
                <article key={relatedPost.slug} className="border border-border rounded-lg p-4 hover:shadow-lg transition">
                  <Badge variant="secondary" className="mb-2">
                    {relatedPost.category}
                  </Badge>
                  <h3 className="text-lg font-semibold mb-2">
                    <Link to={`/nieuws/${relatedPost.slug}`} className="hover:text-primary">
                      {relatedPost.title}
                    </Link>
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    {relatedPost.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{new Date(relatedPost.date).toLocaleDateString("nl-NL")}</span>
                    <span>{relatedPost.readTime} min</span>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}
      </main>
      
      <Footer />
      <NewsletterPopup isOpen={showPopup} onClose={closePopup} />
    </div>
  );
};

export default NieuwsPost;